/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Popover,
    Typography,
    IconButton
} from "@mui/material";
import { styled } from "@mui/system";
import CustomTooltip from "../../../../utils/CustomTooltip";
import SnackBarMessage from "../../../components/snackbar/snackbar";
import styles from "./alertTables.module.css";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandRight from "../../../../assets/images/expandRight.svg";
import AlertPagination from "./AlertPagination";
import { formatText } from "utils/formatText";
import {columnDesc} from "../../../../utils/columndesc";

const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "var(--primany-table-header-color)",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #EAECF0",
        whiteSpace: "nowrap",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

function normalizeOpenPoRows(data) {
    if (!Array.isArray(data)) return [];

    return data.flatMap((entry) => {
        if (!entry || typeof entry !== "object") return [];
        if (Array.isArray(entry.data)) {
            return entry.data
                .filter((row) => row && typeof row === "object" && !Array.isArray(row))
                .map((row) => ({
                    ...row,
                    ...(row.Material_ID == null && entry.Material_ID != null
                        ? { Material_ID: entry.Material_ID }
                        : {}),
                }));
        }
        return Array.isArray(entry) ? [] : [entry];
    });
}

function groupRowsByMaterialId(rows) {
    const groups = [];
    const groupByMaterialId = new Map();

    rows.forEach((row) => {
        const materialId = String(row.Material_ID ?? row["Material Id"] ?? "").trim() || "—";
        let group = groupByMaterialId.get(materialId);
        if (!group) {
            group = { materialId, rows: [] };
            groupByMaterialId.set(materialId, group);
            groups.push(group);
        }
        group.rows.push(row);
    });

    return groups;
}

function paginateMaterialGroups(groups, pageSize) {
    const pages = [];
    let currentPageGroups = [];
    let currentPageSize = 0;

    groups.forEach((group) => {
        if (currentPageGroups.length > 0 && currentPageSize + group.rows.length > pageSize) {
            pages.push(currentPageGroups);
            currentPageGroups = [];
            currentPageSize = 0;
        }
        currentPageGroups.push(group);
        currentPageSize += group.rows.length;
    });

    if (currentPageGroups.length > 0) pages.push(currentPageGroups);
    return pages;
}

const OpenPOTable = ({
    data,
    columnList,
    type,
    paginationObj,
    onPageChange,
    onSortChange,
    sortConfig,
    loading,
    isReadOnly,
    groupByMaterialId,
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isProcessing, setIsProcessing] = useState(false);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: '',
        type: 'message'
    });
    const [expandedColumns, setExpandedColumns] = useState({});
    const [columnInfoAnchor, setColumnInfoAnchor] = useState(null);
    const [columnInfoText, setColumnInfoText] = useState("");

    useEffect(() => {
        if (!paginationObj && !onPageChange) {
            setPage(0);
        }
        setIsProcessing(Boolean(loading));
    }, [data, loading, groupByMaterialId, paginationObj, onPageChange]);

    const closeSnackbar = () => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'message' });
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation(); // Prevent triggering sort
        setExpandedColumns(prev => ({
            ...prev,
            [columnLabel]: !prev[columnLabel]
        }));
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return label === "Material Desc";
    };

     // API-based sorting handler
        const handleSort = useCallback((key, week) => {
            if (onSortChange) {
                setIsProcessing(true);
                const newDirection = sortConfig?.key === key && sortConfig?.direction === "asc"
                    ? "desc"
                    : "asc";
    
                onSortChange({ key, direction: newDirection, week: week || null });
            }
        }, [sortConfig, onSortChange]);

    const sortedData = useMemo(
        () => groupByMaterialId ? normalizeOpenPoRows(data) : data,
        [data, groupByMaterialId],
    );
    const materialGroups = useMemo(
        () => groupByMaterialId ? groupRowsByMaterialId(sortedData) : [],
        [groupByMaterialId, sortedData],
    );
    const materialGroupPages = useMemo(
        () => groupByMaterialId ? paginateMaterialGroups(materialGroups, rowsPerPage) : [],
        [groupByMaterialId, materialGroups, rowsPerPage],
    );

    const handleChangePage = (event, newPage, pageSize = rowsPerPage) => {
        if (paginationObj || onPageChange) {
            setIsProcessing(true);
            onPageChange?.(newPage + 1, pageSize);
            return;
        }
        setPage(newPage);
        setIsProcessing(false);
    };

    const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
         setRowsPerPage(newRowsPerPage);
         setPage(0);
         if (paginationObj || onPageChange) {
             setIsProcessing(true);
             onPageChange?.(1, newRowsPerPage);
         }
     }, [groupByMaterialId, paginationObj, onPageChange]);

    const selectedColumns = columnList.filter((a) => a.visibility === true);

    const getDisplayData = () => {
        if (!groupByMaterialId && (paginationObj || onPageChange)) return sortedData;
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    };

    const isExpandableColumn = (label) => label === "Material Desc" || label === "Site Cluster";

    const getExpandedCellStyle = (isExpanded) => ({
        minWidth: isExpanded ? '250px' : undefined,
        maxWidth: isExpanded ? '250px' : undefined,
        width: isExpanded ? '250px' : undefined,
        whiteSpace: isExpanded ? 'normal' : 'nowrap',
        overflow: isExpanded ? 'visible' : 'hidden',
        textOverflow: isExpanded ? 'clip' : 'ellipsis',
    });

    const renderExpandableCell = (alert, col, isExpanded) => (
        <StyledTableCell
            key={`${alert["Vendor ID"]}-${col.label}`}
            className={!isExpanded ? styles.ellipsisText : ''}
            sx={getExpandedCellStyle(isExpanded)}
        >
            <CustomTooltip title={formatText(alert[col.label])}>
                <span>{col.label === 'CBU' ? alert[col.label] : formatText(alert[col.label])}</span>
            </CustomTooltip>
        </StyledTableCell>
    );

    const renderStandardCell = (alert, col, colIndex) => {
        const isRightAligned = col.label === 'Qty at risk' || col.label === 'Qty. risk';
        const suffix = col.label === 'Qty. risk' ? ' T' : '';

        return (
            <StyledTableCell
                key={colIndex}
                style={{ textAlign: isRightAligned ? 'right' : 'center' }}
            >
                {alert[col.label]}{suffix}
            </StyledTableCell>
        );
    };

    const renderTableCell = (alert, col, colIndex) => {
        if (isExpandableColumn(col.label)) {
            return renderExpandableCell(alert, col, expandedColumns[col.label]);
        }
        return renderStandardCell(alert, col, colIndex);
    };

    const renderTableRow = (alert, rowIndex) => (
        <StyledTableRow key={`alert-row-${alert["PO Number"] || "po"}-${alert["Vendor ID"] || alert["Vendor Name"] || "vendor"}-${alert["Site code"] || "site"}-${rowIndex}`}>
            {selectedColumns.map((col, colIndex) => renderTableCell(alert, col, colIndex))}
        </StyledTableRow>
    );

    const renderGroupedRows = () => {
        const groupsForPage = paginationObj || onPageChange
            ? materialGroups
            : materialGroupPages[page] || [];
        return groupsForPage.flatMap((group, groupIndex) => [
            <StyledTableRow key={`material-group-${group.materialId}-${groupIndex}`} data-testid="material-group-row">
                <StyledTableCell
                    colSpan={selectedColumns.length}
                    sx={{
                        textAlign: "left",
                        fontWeight: 700,
                        backgroundColor: "#F2F4F7",
                        color: "#344054",
                    }}
                    style={{
                        textAlign: 'left',
                        color: 'black',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                    }}
                >
                    {group.materialId}
                </StyledTableCell>
            </StyledTableRow>,
            ...group.rows.map((row, rowIndex) => renderTableRow(row, `${groupIndex}-${rowIndex}`)),
        ]);
    };

    
    
        const columnDescriptionByNormalizedLabel = useMemo(() => {
            const map = new Map();
            Object.entries(columnDesc[type] || {}).forEach(([label, desc]) => {
                map.set(label, desc);
            });
            return map;
        }, [columnDesc]);
    
        const getColumnDescription = useCallback((label) => (
            columnDescriptionByNormalizedLabel.get(label) || ""
        ), [columnDescriptionByNormalizedLabel]);
    
        const handleOpenColumnInfo = (event, label) => {
            event.stopPropagation();
            const description = getColumnDescription(label);
            if (!description) {
                return;
            }
            setColumnInfoText(description);
            setColumnInfoAnchor(event.currentTarget);
        };
    
        const handleCloseColumnInfo = () => {
            setColumnInfoAnchor(null);
            setColumnInfoText("");
        };
    
    

    return (
        <>
            <Box sx={{ position: "relative" }}>
                {/* Loader overlay */}
                {isProcessing && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <TableContainer
                    className={`${styles.table} ${styles.pageScrollTable} ${styles.maiTableHeight}`}
                    style={{ height: "auto" }}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '7px',
                            height: '7px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '7px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '7px',
                        }
                    }}
                >
                    <Table size="small" stickyHeader>
                    <TableHead>
                        {selectedColumns.length > 0 ? (
                            <TableRow>
                                {selectedColumns.map((col, index) => (
                                   <StyledTableCell
                                        key={`col-${index * 2}`}
                                        onClick={() => handleSort(col.label)}
                                            sx={{
                                                position: "sticky",
                                                zIndex: 15,
                                                backgroundColor: "white",
                                                minWidth: expandedColumns[col.label] ? '300px' : (col.width || "100px"),
                                                maxWidth: expandedColumns[col.label] ? '300px' : undefined,
                                                width: expandedColumns[col.label] ? '300px' : (col.width || "100px"),
                                                cursor: "pointer",
                                                 }}
                                    >
                                        <div className={styles.sortIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                {Boolean(getColumnDescription("Total FG Coverage")) && (
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Total FG Coverage info`}
                                                    onClick={(event) => handleOpenColumnInfo(event, "Total FG Coverage")}
                                                    sx={{ padding: '1px', color: '#ffffff' }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                                </IconButton>
                                            )}
                                            <span style={{ flex: 1 }}>{col.label}</span>
                                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                                    {sortConfig?.key === col.label && (
                                                    sortConfig?.direction === 'desc' ? <ArrowDownwardIcon fontSize="12px" /> : <ArrowUpwardIcon fontSize="12px" />
                                                )}
                                                {isTruncatableColumn(col.label) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => toggleColumnExpansion(col.label, e)}
                                                        aria-label="Reset Filters"
                                                        style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
                                                    >
                                                        <span
                                                            title="Expand/Collapse column"
                                                            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                                                        >
                                                            <img src={ExpandRight} alt="expandRight" />
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </StyledTableCell>
                                ))}
                                </TableRow>
                            ) : (
                                <TableRow>
                                    <StyledTableCell>
                                    No columns available...
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {sortedData.length > 0 ? (
                            groupByMaterialId
                                ? renderGroupedRows()
                                : getDisplayData().map(renderTableRow)
                        ) : (
                            <StyledTableRow key="Data not available">
                                <StyledTableCell colSpan={10} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                    {loading ? "Loading..." : "Data not available..."}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Popover
                open={Boolean(columnInfoAnchor)}
                anchorEl={columnInfoAnchor}
                onClose={handleCloseColumnInfo}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Typography sx={{ p: 1.5, fontSize: '12px', maxWidth: '420px', whiteSpace: 'normal' }}>
                    {columnInfoText}
                </Typography>
            </Popover>
            <AlertPagination
                paginationObj={paginationObj}
                onPageChange={onPageChange}
                totalPagesOverride={groupByMaterialId && !paginationObj && !onPageChange
                    ? Math.max(1, materialGroupPages.length)
                    : undefined}
                localPage={page}
                rowsPerPage={rowsPerPage}
                totalRecords={sortedData.length}
                onChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />

            {snackBarObject["snackbarOpen"] && <SnackBarMessage type={snackBarObject["type"]} snackbarMessage={snackBarObject["message"]} snackbarOpen={snackBarObject["snackbarOpen"]} setSnackbarOpen={closeSnackbar} />}
        </>
    );
};

OpenPOTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columnList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
            width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    type: PropTypes.string,
    paginationObj: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        total_pages: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    onSortChange: PropTypes.func,
    sortConfig: PropTypes.shape({
        key: PropTypes.string,
        direction: PropTypes.string,
        week: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
    }),
    loading: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    groupByMaterialId: PropTypes.bool,
    onGroupByMaterialIdChange: PropTypes.func,
};

OpenPOTable.defaultProps = {
    data: [],
    columnList: [],
    type: "",
    paginationObj: null,
    onPageChange: null,
    onSortChange: null,
    sortConfig: { key: "", direction: "asc", week: null },
    loading: false,
    isReadOnly: false,
    groupByMaterialId: false,
    onGroupByMaterialIdChange: () => {},
};

export default OpenPOTable;